import { Link, useLocation } from "react-router-dom";
import { Phone, EnvelopeSimple, Globe, FacebookLogo, InstagramLogo, LinkedinLogo, XLogo, YoutubeLogo, WhatsappLogo } from "@phosphor-icons/react";
import { COMPANY } from "@/lib/data";
import { useSite, phoneHref, whatsappHref } from "@/context/SiteContext";
import { Logo } from "@/components/Logo";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "For Clients", to: "/for-clients" },
  { label: "For Facilities & Partners", to: "/facilities" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

const serviceLinks = [
  { label: "Non-Emergency Medical Transportation", to: "/services/nemt" },
  { label: "Wheelchair Transportation", to: "/services/wheelchair-transportation" },
  { label: "Ambulatory Transportation", to: "/services/ambulatory-transportation" },
  { label: "ODP Transportation", to: "/services/odp-transportation" },
  { label: "Private Pay Transportation", to: "/private-pay" },
  { label: "Facility Transportation", to: "/facilities" },
];

const odpLinks = [
  { label: "Home & Community Habilitation", to: "/odp-services" },
  { label: "Companion Services", to: "/odp-services" },
  { label: "Registered Nursing", to: "/odp-services" },
  { label: "Transportation", to: "/odp-services" },
];

const SOCIALS = [
  { key: "social_facebook", label: "Facebook", Icon: FacebookLogo },
  { key: "social_instagram", label: "Instagram", Icon: InstagramLogo },
  { key: "social_linkedin", label: "LinkedIn", Icon: LinkedinLogo },
  { key: "social_x", label: "X (Twitter)", Icon: XLogo },
  { key: "social_youtube", label: "YouTube", Icon: YoutubeLogo },
];

export function Footer() {
  const content = useSite();
  const location = useLocation();
  const socials = SOCIALS.filter((s) => (content[s.key] || "").trim());
  const waEnabled = content.whatsapp_enabled !== false && (content.whatsapp_number || "").trim();
  const homeClick = (to) => (e) => {
    if (to === "/" && location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  return (
    <footer data-testid="footer" className="noise-overlay bg-brand-blue text-slate-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <Link to="/" onClick={homeClick("/")} data-testid="footer-home-logo" aria-label="CareDeserved home" className="inline-block">
              <Logo variant="light" />
            </Link>
            <p className="mt-4 font-heading text-lg font-semibold text-white">{COMPANY.tagline}</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Safe, reliable, and compassionate non-emergency medical transportation and qualified Pennsylvania ODP services.
            </p>
            {socials.length > 0 && (
              <div className="mt-6" data-testid="footer-social">
                <h4 className="text-sm font-semibold uppercase tracking-wider text-white">Follow CareDeserved</h4>
                <div className="mt-3 flex flex-wrap gap-3">
                  {socials.map(({ key, label, Icon }) => (
                    <a
                      key={key}
                      href={content[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`CareDeserved on ${label}`}
                      data-testid={`social-${key.replace("social_", "")}`}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-brand-teal"
                    >
                      <Icon size={20} weight="fill" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white">Quick Links</h4>
            <ul className="mt-4 space-y-2.5">
              {quickLinks.map((l) => (
                <li key={l.to + l.label}>
                  <Link to={l.to} onClick={homeClick(l.to)} data-testid={l.to === "/" ? "footer-home-link" : undefined} className="text-sm text-slate-400 transition-colors hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white">Services</h4>
            <ul className="mt-4 space-y-2.5">
              {serviceLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-slate-400 transition-colors hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white">ODP Services</h4>
            <ul className="mt-4 space-y-2.5">
              {odpLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-slate-400 transition-colors hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
            <Link to="/odp-services" data-testid="footer-odp-referral"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-purple px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-purple2">
              Make an ODP Referral
            </Link>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white">Contact</h4>
            <ul className="mt-4 space-y-3">
              <li>
                <a href={phoneHref(content.phone)} data-testid="footer-phone" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                  <Phone size={18} weight="fill" className="text-brand-teal2" /> {content.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${content.email}`} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                  <EnvelopeSimple size={18} weight="fill" className="text-brand-teal2" /> {content.email}
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-300">
                <Globe size={18} weight="fill" className="text-brand-teal2" /> {content.website}
              </li>
              {waEnabled && (
                <li>
                  <a href={whatsappHref(content)} target="_blank" rel="noopener noreferrer"
                    data-testid="footer-whatsapp"
                    className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1ebe5d]">
                    <WhatsappLogo size={18} weight="fill" /> Chat With Us on WhatsApp
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} CareDeserved LLC. All Rights Reserved.</p>
          <div className="flex items-center gap-5 text-xs text-slate-400">
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-white">Terms of Use</Link>
            <Link to="/accessibility" className="hover:text-white">Accessibility</Link>
            <Link to="/admin/login" data-testid="footer-staff-login" className="text-slate-500 transition-colors hover:text-slate-300">Staff Login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
